import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationItem } from '~/apis/notificationsApi';
import {
  NOTIFICATIONS_LIST_KEY,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsList,
  useNotificationUnreadCount,
} from '~/hooks/useNotifications';
import { getNotificationPath } from '~/lib/notificationRoutes';
import { NotificationListItem } from './NotificationListItem';

type NotificationPanelProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  enabled: boolean;
};

export function NotificationPanel({ anchorEl, open, onClose, enabled }: NotificationPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const listQuery = useNotificationsList({ enabled: enabled && open, unreadOnly });
  const unreadQuery = useNotificationUnreadCount(enabled);
  const unreadCount = unreadQuery.data ?? 0;
  const markReadMutation = useMarkNotificationRead();
  const markAllMutation = useMarkAllNotificationsRead();

  const items = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [listQuery.data?.pages]
  );

  const total = listQuery.data?.pages[0]?.total ?? 0;
  const hasMore = listQuery.hasNextPage;

  const handleItemClick = async (item: NotificationItem) => {
    onClose();
    const path = getNotificationPath(item.entityType, item.entityId);

    if (item.readAt == null) {
      try {
        await markReadMutation.mutateAsync(item.id);
      } catch {
        return;
      }
    }

    if (path) {
      navigate(path);
    }
  };

  const handleMarkAllRead = () => {
    markAllMutation.mutate();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        unreadQuery.refetch(),
        queryClient.resetQueries({ queryKey: [...NOTIFICATIONS_LIST_KEY, { unreadOnly }] }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const showListLoading =
    listQuery.isPending || (isRefreshing && !listQuery.isFetchingNextPage);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: { xs: 'calc(100vw - 24px)', sm: 520 },
            maxWidth: { xs: 'calc(100vw - 24px)', sm: 560 },
            maxHeight: { xs: 'calc(100vh - 96px)', sm: 'min(720px, calc(100vh - 96px))' },
            minHeight: { xs: 360, sm: 480 },
            display: 'flex',
            flexDirection: 'column',
            mt: 0.5,
          },
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="Refresh notifications">
              <span>
                <IconButton
                  size="small"
                  onClick={() => void handleRefresh()}
                  disabled={isRefreshing || !open}
                  aria-label="Refresh notifications"
                >
                  {isRefreshing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0 || markAllMutation.isPending || isRefreshing}
            >
              Mark all as read
            </Button>
          </Stack>
        </Stack>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={unreadOnly}
              onChange={(_, checked) => setUnreadOnly(checked)}
            />
          }
          label="Unread only"
          sx={{ mt: 0.5, ml: 0 }}
        />
      </Box>
      <Divider />
      <Box sx={{ overflow: 'auto', flex: 1, minHeight: { xs: 240, sm: 360 } }}>
        {showListLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : listQuery.isError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Could not load notifications.
          </Alert>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {unreadOnly ? 'No unread notifications.' : 'No notifications yet.'}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => (
              <Box key={item.id}>
                <NotificationListItem item={item} onClick={() => handleItemClick(item)} />
                <Divider component="li" />
              </Box>
            ))}
          </List>
        )}
      </Box>
      {hasMore && !listQuery.isPending ? (
        <Box sx={{ p: 1.5, flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            size="small"
            onClick={() => listQuery.fetchNextPage()}
            disabled={listQuery.isFetchingNextPage}
          >
            {listQuery.isFetchingNextPage
              ? 'Loading…'
              : `Load more (${items.length} of ${total})`}
          </Button>
        </Box>
      ) : null}
    </Popover>
  );
}
